
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserApplicationEntity from 'Models/Security/Acl/UserApplicationEntity';
import VisitorsApplicationEntity from 'Models/Security/Acl/VisitorsApplicationEntity';
import { IAcl } from 'Models/Security/IAcl';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import * as Validators from 'Validators';
import { FileListPreview } from 'Views/Components/CRUD/Attributes/AttributeFile';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';

import type { CountryCode } from 'Models/Enums/CountryCodeEnum';
import { FormFieldDefinition } from 'Views/Components/TemplateForm/TemplateFormTypes';
import { isPhone } from '../../Validators/Functions/Custom';
import { isEmail } from '../../Validators/Functions/Email';
import { CustomerApplicationTemplateEmailRuleEntity } from './CustomerApplicationTemplateEmailRuleEntity';
export type BusinessCreditReportSettings = {
	acnBusinessCreditReportSettingIds?: string[];
	abnBusinessCreditReportSettingIds?: string[];
}

export type CreditReportAmountBand = {
	minAmount?: number;
	maxAmount?: number;
	settings?: BusinessCreditReportSettings;
}

export type AutomatedCreditReportGenerationConfig = {
	enabled: boolean;
	bands?: CreditReportAmountBand[];
}

export type ChecklistItem = {
	id: string;
	displayName: string;
}

export type ChecklistTemplate = {
	title: string;
	enabled: boolean | null;
	items: ChecklistItem[];
}

export const defaultChecklistTemplate = (): ChecklistTemplate => ({
	title: 'Checklist',
	enabled: null,
	items: [],
});


export interface IApplicationEntityAttributes extends IModelAttributes {
	privacyPolicyId: string;
	privacyPolicy: Blob;
	contactName: string;
	isActive: boolean;
	email: string;
	phone: string;
	applicationName: string;
	logoId: string;
	logo: Blob;
	termsAndConditionsId: string;
	termsAndConditions: Blob;
	legacyID: string;

	businessEntityId: string;
	businessEntity: Models.BusinessEntity | Models.IBusinessEntityAttributes;
	jsonSchema: string;
	tradeReferenceTemplateSchema: FormFieldDefinition[];
	countryCode: CountryCode;
	emailFooterHtml: string;
	automatedCreditReportGeneration: AutomatedCreditReportGenerationConfig | null;
	checklistTemplate: ChecklistTemplate | null;
	creditRequestedMultiplier: number;
	emailRules: CustomerApplicationTemplateEmailRuleEntity[];
	assessmentConfigId?: string | null;
	assessmentConfigEnabled?: boolean;
	ppsrConfigurationId?: string | null;
}

@entity('ApplicationEntity', 'Application')
export default class ApplicationEntity extends Model
	implements IApplicationEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserApplicationEntity(),
		new VisitorsApplicationEntity(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * Approve Privacy Policy
	 */
	@observable
	@attribute<ApplicationEntity, string>({ file: 'privacyPolicy' })
	@CRUD({
		name: 'Privacy Policy',
		displayType: 'file',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseUuid,
		fileAttribute: 'privacyPolicy',
		displayFunction: attr => attr
			? <FileListPreview url={attr} />
			: 'No File Attached',
	})
	public privacyPolicyId: string;

	@observable
	public privacyPolicy: Blob;

	/**
	 * Contact Name
	 */
	@observable
	@attribute<ApplicationEntity, string>()
	@CRUD({
		name: 'Contact Name',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public contactName: string;

	/**
	 * Is the application active
	 */
	@observable
	@attribute<ApplicationEntity, boolean>()
	@CRUD({
		name: 'Is Active',
		displayType: 'checkbox',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public isActive: boolean;

	@observable
	@attribute<ApplicationEntity, string>()
	@CRUD({
		name: 'Email',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public email: string;

	@observable
	@attribute<ApplicationEntity, string>()
	@CRUD({
		name: 'Phone',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public phone: string;

	@observable
	@attribute<ApplicationEntity, string>()
	@CRUD({
		name: 'Application Name',
		displayType: 'textfield',
		order: 60,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public applicationName: string;

	/**
	 * Business Logo
	 */
	@observable
	@attribute<ApplicationEntity, string>({ file: 'logo' })
	@CRUD({
		name: 'Logo',
		displayType: 'file',
		order: 70,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseUuid,
		fileAttribute: 'logo',
		displayFunction: attr => attr
			? <FileListPreview url={attr} />
			: 'No File Attached',
	})
	public logoId: string;

	@observable
	public logo: Blob;

	/**
	 * Application Terms and Conditions
	 */
	@observable
	@attribute<ApplicationEntity, string>({ file: 'termsAndConditions' })
	@CRUD({
		name: 'Terms and Conditions',
		displayType: 'file',
		order: 80,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseUuid,
		fileAttribute: 'termsAndConditions',
		displayFunction: attr => attr
			? <FileListPreview url={attr} />
			: 'No File Attached',
	})
	public termsAndConditionsId: string;

	@observable
	public termsAndConditions: Blob;

	@Validators.Unique()
	@observable
	@attribute<ApplicationEntity, string>()
	@CRUD({
		name: 'Legacy ID',
		displayType: 'textfield',
		order: 90,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public legacyID: string;

	/**
	 * Approve Applications
	 */
	@observable
	@attribute()
	@CRUD({

		name: 'Business Entity',
		displayType: 'reference-combobox',
		order: 110,
		referenceTypeFunc: () => Models.BusinessEntity,
	})
	public businessEntityId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntity: Models.BusinessEntity;

	@action public validateField = async (field: string) => {
		switch (field) {
			case 'phone': {
				if (this.phone && !isPhone(this.phone)) {
					this.setFieldError('phone', true, 'Phone number must be valid');
				} else {
					this.setFieldError('phone', false, '');
				}
				break;
			}
			case 'email': {
				if (!this.email) {
					this.setFieldError('email', true, 'Email address cannot be empty');
				} else if (!isEmail(this.email)) {
					this.setFieldError('email', true, 'Email address must be valid');
				} else {
					this.setFieldError('email', false, '');
				}
				break;
			}
			case 'applicationName':
				if (!this.applicationName) {
					this.setFieldError('applicationName', true, 'Application name cannot be empty');
				} else {
					this.setFieldError('applicationName', false, '');
				}
				break;
			case 'jsonSchema': {
				if (this.jsonSchema) {
					try {
						JSON.parse(this.jsonSchema);
						this.setFieldError('jsonSchema', false, '');
					} catch (err) {
						this.setFieldError('jsonSchema', true, 'Invalid json schema');
					}
				} else {
					this.setFieldError('jsonSchema', false, '');
				}
				break;
			}
		}
	}

	public validateAllFields = async () => {
		const fieldList = ['email', 'applicationName'];
		const validationPromises = fieldList.map(field => this.validateField(field));
		await Promise.all(validationPromises);
	}

	@observable
	@attribute<ApplicationEntity, string>()
	@CRUD({
		name: 'Json Schema',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public jsonSchema: string;

	// Default value for this.jsonSchema
	public static DefaultJsonSchema = "[]";

	@observable
	public tradeReferenceTemplateSchema: FormFieldDefinition[];

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IApplicationEntityAttributes>) {
		super(attributes);
	}

	@observable
	@attribute<ApplicationEntity, CountryCode>()
	public countryCode: CountryCode;

	@observable
	@attribute<ApplicationEntity, string>()
	public emailFooterHtml: string;

	@observable
	@attribute<ApplicationEntity, AutomatedCreditReportGenerationConfig | null>({ 
		// Serialise to json string BEFORE send it in http request for GQL
		serialise: (_, a) => a !== null ? JSON.stringify(a) : null 
	})
	public automatedCreditReportGeneration: AutomatedCreditReportGenerationConfig | null = null;

	@observable
	public checklistTemplate: ChecklistTemplate | null = null;

	@observable
	@attribute<ApplicationEntity, number>()
	@CRUD({
		name: 'Credit Requested Multiplier',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public creditRequestedMultiplier: number;

	@observable
	public emailRules: CustomerApplicationTemplateEmailRuleEntity[] = [];

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IApplicationEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.privacyPolicy !== undefined) {
				this.privacyPolicy = attributes.privacyPolicy;
			}
			if (attributes.privacyPolicyId !== undefined) {
				this.privacyPolicyId = attributes.privacyPolicyId;
			}
			if (attributes.contactName !== undefined) {
				this.contactName = attributes.contactName;
			}
			if (attributes.isActive !== undefined) {
				this.isActive = attributes.isActive;
			}
			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}
			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.applicationName !== undefined) {
				this.applicationName = attributes.applicationName;
			}
			if (attributes.logo !== undefined) {
				this.logo = attributes.logo;
			}
			if (attributes.logoId !== undefined) {
				this.logoId = attributes.logoId;
			}
			if (attributes.termsAndConditions !== undefined) {
				this.termsAndConditions = attributes.termsAndConditions;
			}
			if (attributes.termsAndConditionsId !== undefined) {
				this.termsAndConditionsId = attributes.termsAndConditionsId;
			}
			if (attributes.legacyID !== undefined) {
				this.legacyID = attributes.legacyID;
			}

			if (attributes.businessEntityId !== undefined) {
				this.businessEntityId = attributes.businessEntityId;
			}
			if (attributes.businessEntity !== undefined) {
				if (attributes.businessEntity === null) {
					this.businessEntity = attributes.businessEntity;
				} else if (attributes.businessEntity instanceof Models.BusinessEntity) {
					this.businessEntity = attributes.businessEntity;
					this.businessEntityId = attributes.businessEntity.id;
				} else {
					this.businessEntity = new Models.BusinessEntity(attributes.businessEntity);
					this.businessEntityId = this.businessEntity.id;
				}
			}

			if (attributes.jsonSchema !== undefined) {
				this.jsonSchema = attributes.jsonSchema;
			}
			if (attributes.countryCode != null) {
				this.countryCode = attributes.countryCode;
			}
			if (attributes.emailFooterHtml !== undefined) {
				this.emailFooterHtml = attributes.emailFooterHtml;
			}
			if (attributes.automatedCreditReportGeneration !== undefined) {
				this.automatedCreditReportGeneration = typeof attributes.automatedCreditReportGeneration === 'string'
					? JSON.parse(attributes.automatedCreditReportGeneration)
					: attributes.automatedCreditReportGeneration;
			}
			if (attributes.checklistTemplate !== undefined) {
				this.checklistTemplate = typeof attributes.checklistTemplate === 'string'
					? JSON.parse(attributes.checklistTemplate)
					: attributes.checklistTemplate;
			}
			if (attributes.creditRequestedMultiplier !== undefined) {
				this.creditRequestedMultiplier = attributes.creditRequestedMultiplier;
			}

			if (attributes.emailRules !== undefined) {
				this.emailRules = attributes.emailRules;
			}
			if (attributes.assessmentConfigId !== undefined) {
				this.assessmentConfigId = attributes.assessmentConfigId;
			}
			if (attributes.assessmentConfigEnabled !== undefined) {
				this.assessmentConfigEnabled = attributes.assessmentConfigEnabled;
			}
			if (attributes.ppsrConfigurationId !== undefined) {
				this.ppsrConfigurationId = attributes.ppsrConfigurationId;
			}
		}
	}

	@observable
	public assessmentConfigId?: string | null;

	@observable
	public assessmentConfigEnabled?: boolean;

	@observable
	public ppsrConfigurationId?: string | null;

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		businessEntity {
			${Models.BusinessEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
				contentType: 'multipart/form-data',
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ApplicationEntity.prototype, 'created');
CRUD(modifiedAttr)(ApplicationEntity.prototype, 'modified');
